<script setup>

import {router, usePage} from "@inertiajs/vue3"
import {computed, ref} from "vue"
import {helpers} from "@/Services/helpers.js"
import CDialog from "@/Components/CDialog.vue"
import {VNumberInput} from 'vuetify/labs/VNumberInput'
import CCustomizedProductDetailsModal from "@/Pages/Cart/Partials/CCustomizedProductDetailsModal.vue"


const drawer = defineModel()

const page = usePage()
const checkoutModal = ref(false)
const detailsDialog = ref(false)
const dialogMetadata = ref(null)

const groupedCartItems = computed(() => {
    return page.props.cartItems.reduce((groups, item) => {
        const productTitle = item.product_variation.product.title
        const attributes = item.product_variation.attribute_values

        if (!groups[productTitle]) {
            groups[productTitle] = {variations: {}, single: []}
        }

        if (attributes.length === 2) {
            // Produto com 2 atributos (ex: Cor e Tamanho)
            const color = attributes.find(attribute => attribute.attribute_id === 1).title
            const size = attributes.find(attribute => attribute.attribute_id === 2).title

            if (!groups[productTitle].variations[color]) {
                groups[productTitle].variations[color] = []
            }

            groups[productTitle].variations[color].push({
                ...item,
                size: size
            })
        }

        if (attributes.length === 1) {
            // Produto com apenas 1 atributo (ex: Tamanho ou Cor)
            groups[productTitle].single.push(item)
        }

        if (attributes.length === 0) {
            groups[productTitle].single.push(item)
        }

        return groups
    }, {})
})

const checkout = function () {
    drawer.value = false
    router.get(
        route('cart.checkout'),
        {},
        {
            preserveState: true
        }
    )
}

const deleteCartItem = function (cartItem) {
    router.get(
        route('cart.delete', cartItem),
        {},
        {
            preserveState: true
        }
    )
}

const deleteSessionItem = function (variantProductId) {
    router.get(
        route('cart.sessionDelete', variantProductId),
        {},
        {
            preserveState: true
        }
    )
}

const updateVariantQuantity = function (cartItem) {
    router.post(
        route('cart.update'),
        {cartItem},
        {
            preserveState: true,
            preserveScroll: true,
        }
    )
}

const getOrderSubTotal = function () {
    let cartItems = page.props.cartItems
    let totalValue = 0

    cartItems.forEach(function (item) {
        totalValue += item.subtotal
    })

    return totalValue
}

const calculateVariationsQuantity = function (colorCartGroup, color) {
    let totalGroupQuantity = 0

    // Verifica se a cor existe no grupo
    if (colorCartGroup[color]) {
        colorCartGroup[color].forEach(cartItem => {
            totalGroupQuantity += cartItem.quantity
        })
    }

    return totalGroupQuantity
}

const calculateVariationsPrice = function (colorCartGroup, color) {
    let totalGroupPrice = 0

    // Verifica se a cor existe no grupo
    if (colorCartGroup[color]) {
        colorCartGroup[color].forEach(cartItem => {
            totalGroupPrice += cartItem.subtotal
        })
    }

    return totalGroupPrice
}

const redirectToProductDetails = function (cartItem) {
    const slug = cartItem?.single?.[0]?.product_variation?.product?.slug ||
        Object.values(cartItem?.variations)?.[0]?.[0]?.product_variation?.product?.slug

    if (slug) {
        router.get(
            route('product.view', {slug}),
            {},
            {
                onSuccess: () => {
                    drawer.value = false
                },
                onError: () => {
                    dialog.value.alert('Ocorreu um erro ao carregar o produto. Contate um administrador do portal.')
                }
            }
        )
    } else {
        dialog.value.alert('Ocorreu um erro ao carregar o produto. Contate um administrador do portal.')
    }
}
</script>

<template>
    <v-list lines="two" class="mx-6" :class="{ 'pt-10': $vuetify.display.mobile }" style="overflow-x: hidden">
        <v-list-item-title class="pa-5 font-weight-bold" inset>
            <v-row>
                <v-col cols="10" class="pt-5">
                    Lista de Orçamento
                </v-col>
                <v-col>
                    <v-btn icon="mdi-close" variant="flat" @click="drawer = false" />
                </v-col>
            </v-row>
        </v-list-item-title>

        <!-- Percorre os produtos -->
        <template v-for="(groupedCartItem, productTitle) in groupedCartItems" :key="productTitle">
            <v-list-item>
                <template v-slot:prepend>
                    <v-img v-if="groupedCartItem.single[0]?.product_variation.product.medias[0]"
                           :src="groupedCartItem.single[0].product_variation.product.medias[0]?.url"
                           cover width="60" max-width="60"/>

                    <v-img v-if="Object.values(groupedCartItem.variations)[0]?.[0]?.product_variation.product.medias[0]"
                           :src="Object.values(groupedCartItem.variations)[0][0].product_variation.product.medias[0]?.url"
                           cover width="60" max-width="60"/>
                </template>
                <span @click="redirectToProductDetails(groupedCartItem)" class="cursor-pointer">
                        <v-list-item-title class="pl-2 text-h6">{{ productTitle }}</v-list-item-title>
                    </span>
            </v-list-item>

            <!-- Percorre as variações (produtos com 2 atributos) -->
            <v-list-group v-for="(colorGroup, color) in groupedCartItem.variations" :key="color" class="pa-0 ma-0">
                <template v-slot:activator="{ props, isOpen }">
                    <v-list-item v-bind="props" class="pa-1">
                        <v-row class="ma-0">
                            <v-col cols="3" class="d-flex align-center justify-end">
                                <v-tooltip location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-icon v-bind="props">
                                            {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
                                        </v-icon>
                                    </template>
                                    <span>{{ isOpen ? 'Recolher' : 'Expandir' }}</span>
                                </v-tooltip>
                            </v-col>
                            <v-col cols="9" class="pl-0">
                                <v-img
                                    v-if="colorGroup[0].img_url ?? colorGroup[0].product_variation.medias[0]?.url"
                                    :src="colorGroup[0].img_url ?? colorGroup[0].product_variation.medias[0]?.url"
                                    width="50" height="50" class="pr-2 d-inline-block"
                                    style="vertical-align: middle"
                                />
                                <v-list-item-title class="d-inline-block font-weight-bold"
                                                   style="vertical-align: middle">
                                    {{ calculateVariationsQuantity(groupedCartItem.variations, color) }} x
                                    {{ helpers.firstLetterUppercase(color) }}
                                </v-list-item-title>
                            </v-col>
                        </v-row>
                        <template v-slot:append v-if="colorGroup[0]?.price">
                            <v-sheet class="text-end" style="line-height: 1.2rem">
                                <span class="font-weight-bold">
                                    R$ {{ helpers.formatPrice(calculateVariationsPrice(groupedCartItem.variations, color)) }}
                                </span>
                                <br/>
                                <span class="font-weight-bold text-medium-emphasis text-caption">
                                    un R$ {{ helpers.formatPrice(colorGroup[0]?.price) }}
                                </span>
                            </v-sheet>
                        </template>
                    </v-list-item>
                </template>

                <!-- Lista de produtos dentro dessa variação -->
                <v-list-item v-for="cartItem in colorGroup"
                             :key="cartItem.product_variation.id || cartItem.product_variation_id"
                             style="min-height: 16px"
                             class="pa-1">
                    <v-row class="ma-0">
                        <v-col cols="2" class="d-flex align-center justify-end pa-1">
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                    <v-icon v-bind="props" variant="plain" icon="mdi-trash-can" size="18"
                                            class="mr-2"
                                            @click.once="cartItem.id ? deleteCartItem(cartItem.id) : deleteSessionItem(cartItem.product_variation.id)"
                                    />
                                </template>
                                <span>Remover item</span>
                            </v-tooltip>
                        </v-col>
                        <v-col cols="10" class="d-flex align-center pa-1">
                            <v-number-input
                                v-model="cartItem.quantity"
                                controlVariant="split"
                                hideInput
                                inset
                                style="display: inline-flex; min-width: 20px; max-width: 30px; margin: 0 4px;"
                                density="compact"
                                variant="outlined"
                                @update:modelValue="updateVariantQuantity(cartItem)"
                            />
                            <v-list-item-subtitle class="font-weight-bold pl-3">
                                {{ cartItem.quantity }} x Tamanho: {{ cartItem.size?.toUpperCase() ?? '' }}
                            </v-list-item-subtitle>
                        </v-col>
                    </v-row>
                </v-list-item>
            </v-list-group>

            <!-- Produtos com apenas 1 atributo (single) -->
            <v-list-item v-for="cartItemSingle in groupedCartItem.single"
                         :key="cartItemSingle.product_variation.id || cartItemSingle.product_variation_id" class="pa-1">
                <v-row>
                    <v-col cols="3" class="d-flex align-center justify-end">
                        <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                                <v-icon
                                    v-bind="props"
                                    variant="plain"
                                    icon="mdi-trash-can"
                                    size="22"
                                    style="margin-left: 2px"
                                    @click.once="cartItemSingle.id ? deleteCartItem(cartItemSingle.id) : deleteSessionItem(cartItemSingle.product_variation.id)"
                                />
                            </template>
                            <span>Remover item</span>
                        </v-tooltip>
                    </v-col>

                    <v-col cols="9" class="pl-0 d-flex align-center justify-start">
                        <v-img
                            v-if="cartItemSingle.img_url ?? cartItemSingle.product_variation.medias[0]?.url"
                            :src="cartItemSingle.img_url ?? cartItemSingle.img_url ?? cartItemSingle.product_variation.medias[0]?.url"
                            cover
                            width="60"
                            max-width="60"
                            class="pr-2"
                        />
                        <span
                            class="font-weight-bold"
                        >
                            {{ cartItemSingle.quantity }} x
                        </span>

                        <v-number-input
                            v-model="cartItemSingle.quantity"
                            controlVariant="stacked"
                            hideInput
                            inset
                            max-width="20"
                            style="display: inline-flex; min-width: 20px; max-width: 30px; margin: 0 4px;"
                            density="compact"
                            variant="outlined"
                            @update:modelValue="updateVariantQuantity(cartItemSingle)"
                        />

                        <span class="font-weight-bold pl-4 pl-sm-4">
                            {{
                                cartItemSingle.product_variation?.attribute_values?.[0]?.attribute_id === 2
                                    ? "Tamanho:" + cartItemSingle.product_variation?.attribute_values?.[0]?.title.toUpperCase()
                                    : helpers.firstLetterUppercase(
                                        cartItemSingle.product_variation?.attribute_values?.[0]?.title ??
                                        "Sem Cor"
                                    )
                            }}
                        </span>

                        <template v-if="cartItemSingle?.metadata">
                            <v-icon @click="detailsDialog = true; dialogMetadata = cartItemSingle.metadata" class="pl-10" icon="mdi-eye-outline"
                                    title="Detalhes do item" size="22"/>

                            <v-dialog v-model="detailsDialog" location="center" width="auto">
                                <c-customized-product-details-modal v-model="detailsDialog" :metadata="dialogMetadata"/>
                            </v-dialog>
                        </template>
                    </v-col>
                </v-row>

                <template v-if="cartItemSingle.price" v-slot:append>
                    <v-sheet class="text-end" style="line-height: 1.2rem">
                            <span class="font-weight-bold">
                                R$ {{ helpers.formatPrice(cartItemSingle.subtotal) }}
                            </span><br/>
                        <span class="font-weight-bold text-medium-emphasis text-caption">
                                un R$ {{ helpers.formatPrice(cartItemSingle.price) }}
                            </span>
                    </v-sheet>
                </template>
            </v-list-item>
        </template>
        <v-row class="pl-5 pt-5 pb-5" v-if="page.props.cartItems.length > 0">
            <v-col md="12" class="d-flex justify-end"
                   v-if="page.props.cartItems[0].price">
                <span class="font-weight-bold"> Total: R$ {{ helpers.formatPrice(getOrderSubTotal()) }}</span>
            </v-col>
        </v-row>

        <v-row class="pl-5 pt-5 pb-5" v-if="page.props.cartItems.length > 0">
            <v-col md="12" class="d-flex justify-end">
                <v-btn variant="flat" color="secondary" @click="drawer = false" class="font-weight-bold">
                    Continuar navegando
                </v-btn>
                <v-btn variant="flat" color="primary" @click="checkout()" class="font-weight-bold ml-3">
                    Finalizar Orçamento
                </v-btn>
            </v-col>
        </v-row>
        <p v-if="page.props.cartItems.length === 0" class="pa-10">Você ainda não adicionou itens</p>
    </v-list>

    <c-dialog ref="dialog"/>

</template>